import type { ReactNode } from 'react';
import { usePermissions } from './usePermissions';
import type { Permission } from './types';

interface PermissionGateProps {
  permission: Permission;
  /**
   * Rendered when the user does not have the required permission.
   * Defaults to null (renders nothing).
   */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Conditionally renders children based on the current user's permissions.
 * Use this for in-component UI gating (buttons, sections, badges).
 * For route-level protection use RequirePermission instead.
 *
 * @example — hide a button for users without access
 * <PermissionGate permission="leads.form_builder">
 *   <CreateFormButton />
 * </PermissionGate>
 *
 * @example — show an upgrade prompt as the fallback
 * <PermissionGate
 *   permission="ai.visibility"
 *   fallback={<UpgradePrompt />}
 * >
 *   <AIVisibilityDashboard />
 * </PermissionGate>
 */
export const PermissionGate = ({
  permission,
  fallback = null,
  children,
}: PermissionGateProps) => {
  const { can } = usePermissions();
  return can(permission) ? <>{children}</> : <>{fallback}</>;
};
